<template>
  <div class="bg-white rounded-2xl border border-gray-200 p-4 md:p-6 shadow-lg">
    <div v-if="inquiryStep === 'success'" class="text-center">
      <div class="flex justify-center mb-4">
        <CheckCircle class="w-12 h-12 text-green-500" />
      </div>
      <h2 class="text-xl font-bold text-gray-900 mb-2">
        {{ t('property.form.success_title') }}
      </h2>
      <div class="bg-gray-50 rounded-2xl p-6 mb-6">
        <p class="text-gray-600 text-sm">
          {{ t('property.form.success_message', { agent: selectedAgent?.company?.general?.name || agents[0]?.company?.general?.name }) }}
        </p>
      </div>
      <!--
      <div v-if="inquiryResult?.shareUrl">
        <p class="text-sm text-gray-600 mb-4">{{ t('property.form.mypage_info') }}</p>
        <Button as-child="a" :href="inquiryResult.shareUrl" target="_blank" class="w-full bg-brand text-white">
          {{ t('property.form.open_mypage') }}
        </Button>
      </div>
      -->
      <Button type="button" variant="outline" class="w-full mt-4" @click="handleReset">
        {{ t('property.form.close') }}
      </Button>
    </div>

    <div v-else-if="inquiryStep === 'verification'" class="text-center py-6">
      <HelpCircle class="w-12 h-12 text-brand mx-auto mb-4" />
      <h3 class="text-xl font-bold text-gray-900 mb-2">{{ t('property.form.not_finished') || 'まだ完了しておりません' }}</h3>
      <p class="text-sm text-gray-600 mb-6">
        {{ t('property.form.verification_sent') || '認証コードをメールで送信しました。認証コードを入力して問い合わせを完了してください。' }}
      </p>
      <ValidationCode @change="handleCodeChange" />
      <p v-if="inquiryResult?.code === 'failed'" class="text-red-600 text-sm mt-2">
        {{ t('property.form.invalid_code') || '認証コードが正しくないか、期限切れです' }}
      </p>
      <Button
        variant="outline"
        class="mt-6 w-full"
        @click="inquiryStep = 'form'"
      >
        {{ t('property.form.back_to_form') || 'フォームに戻る' }}
      </Button>
    </div>

    <template v-else>
      <div class="text-center mb-4 md:mb-6">
        <div class="text-sm text-gray-600 mb-1 md:mb-2">
          {{ t('property.have_questions') }}
        </div>
        <div class="text-lg font-semibold text-gray-900 mb-2 md:mb-4">
          {{ t('property.request_more_info') }}
        </div>
      </div>

      <div class="space-y-3 md:space-y-4">
        <div
          v-for="agent in agents"
          :key="agent.id"
          class="agent-item border border-gray-200 rounded-2xl p-3 md:p-4 cursor-pointer transition-all"
          :class="{ 'ring-2 ring-brand shadow-sm': selectedAgentId === agent.id }"
          @click="selectedAgentId = agent.id"
        >
          <div class="flex items-start gap-4">
            <ImageWithFallback
              :src="getAvatar(agent)"
              :alt="agent.profile?.personal?.fullname"
              class="w-16 h-16 rounded-full object-cover"
              :width="64" :height="64"
              loading="lazy"
            />
            <div class="flex-1 min-w-0">
              <div class="flex justify-between items-start">
                <div class="min-w-0">
                  <div class="font-semibold text-gray-900 truncate max-w-50">
                    {{ agent.profile?.personal?.fullname }}
                  </div>
                  <div class="text-sm text-gray-600 truncate max-w-50">
                    {{ agent.company?.general?.name }}
                  </div>
                  <div v-if="getBrokerageLicense(agent)" class="text-xs text-gray-400 mt-0.5 truncate max-w-64">
                    {{ getBrokerageLicense(agent) }}
                  </div>
                </div>
              </div>
              <a :href="`/agent/${agent.organizationId}/${agent.id}`" class="text-sm text-brand hover:text-brand/80 transition" @click.stop>{{ t('agent.viewProfile') }}</a>
              <div v-if="selectedAgentId !== agent.id" class="mt-5 text-brand text-sm font-semibold flex items-center gap-1 hover:text-brand/80 transition">
                {{ t('agent.inquire_this_agent') }}
                <ChevronRight :size="16" />
              </div>
            </div>
          </div>

          <!-- Quick Contact Form -->
          <div v-if="selectedAgentId === agent.id" class="mt-3 md:mt-4 pt-3 md:pt-4 border-t border-gray-200">
            <form ref="form" class="space-y-3 md:space-y-4" @submit.prevent="handleInquirySubmit">
              <div class="space-y-1 md:space-y-2 mb-2 md:mb-4">
                <p class="text-sm font-bold text-gray-700">{{ t('property.form.inquiry_content') || 'お問い合わせ内容' }}</p>
                <div v-for="purpose in purposes" :key="purpose.value" class="flex items-center gap-2">
                  <input
                    :id="purpose.value"
                    v-model="inquiryForm.purpose"
                    type="radio"
                    name="purpose"
                    :value="purpose.value"
                    class="text-brand focus:ring-brand"
                  />
                  <label :for="purpose.value" class="text-sm text-gray-700">{{ purpose.label }}</label>
                </div>
              </div>

              <div class="grid grid-cols-2 gap-1 md:gap-2">
                <input
                  v-model="inquiryForm.lastName"
                  type="text"
                  :placeholder="t('property.form.last_name')"
                  required
                  class="w-full px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                />
                <input
                  v-model="inquiryForm.firstName"
                  type="text"
                  :placeholder="t('property.form.first_name')"
                  required
                  class="w-full px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                />
              </div>
              <div>
                <input
                  ref="emailInput"
                  v-model="inquiryForm.email"
                  type="email"
                  :placeholder="t('property.form.email_address')"
                  required
                  class="w-full px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                />
              </div>
              <div>
                <input
                  ref="phoneInput"
                  v-model="inquiryForm.phone"
                  type="tel"
                  :placeholder="t('property.form.phone_number')"
                  class="w-full px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
                />
              </div>
              <div>
                <textarea
                  v-model="inquiryForm.notes"
                  rows="4"
                  :placeholder="t('property.form.message')"
                  class="w-full px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent resize-none"
                  :required="inquiryForm.purpose === 'その他問い合わせ'"
                ></textarea>
              </div>
              <Button
                type="submit"
                class="w-full bg-brand text-white hover:bg-brand/90 cursor-pointer"
                :disabled="inquirySubmitting"
              >
                <span v-if="inquirySubmitting">{{ t('property.form.sending') }}</span>
                <span v-else>{{ t('property.form.send_message') }}</span>
              </Button>
            </form>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, useTemplateRef, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { CheckCircle, HelpCircle, ChevronRight } from 'lucide-vue-next';
import Button from './Button.vue';
import ImageWithFallback from './ImageWithFallback.vue';
import ValidationCode from './ValidationCode.vue';
import { useInquiryForm } from '../composables/useInquiryForm';
import type { Deal, AgentWithProfiles } from '@repo/types';

const props = defineProps<{
  deal: Ref<Deal | undefined>
  agents: AgentWithProfiles[]
  app?: string
}>();

const emit = defineEmits(['success', 'reset']);

const { t } = useI18n();
const selectedAgentId = ref<number | string | null>(null);

const selectedAgent = computed(() =>
  props.agents.find(a => a.id === selectedAgentId.value) || props.agents[0]
);

const {
  form: inquiryForm,
  submitting: inquirySubmitting,
  step: inquiryStep,
  submit: submitInquiry,
  result: inquiryResult,
  reset: resetInquiry,
} = useInquiryForm(props.deal, selectedAgent, { app: props.app || 'openroom' });

const form = useTemplateRef<HTMLFormElement>('form')
const emailInput = ref<HTMLInputElement[]>([]);
const phoneInput = ref<HTMLInputElement[]>([]);

watch(
  () => inquiryForm.email,
  () => emailInput.value[0]?.setCustomValidity('')
);
watch(
  () => inquiryForm.phone,
  () => phoneInput.value[0]?.setCustomValidity('')
);

const handleInquirySubmit = async () => {
  if (emailInput.value[0]) emailInput.value[0].setCustomValidity('');
  if (phoneInput.value[0]) phoneInput.value[0].setCustomValidity('');

  const resp = await submitInquiry();
  if (resp.code === 'failed' && resp.error) {
    if (resp.error.code === 'INVALID_EMAIL' && emailInput.value[0]) {
      emailInput.value[0].setCustomValidity(t('property.form.invalid_email') || 'Invalid email');
    } else if (resp.error.code === 'INVALID_PHONE' && phoneInput.value[0]) {
      phoneInput.value[0].setCustomValidity(t('property.form.invalid_phone') || 'Invalid phone');
    }
    form.value[0]?.reportValidity()
  }

  if (resp.code === 'succeeded') {
    emit('success', resp);
  }
};

const handleCodeChange = (code: string) => {
  if (code.length === 4) {
    submitInquiry(code);
  }
};

const handleReset = () => {
  resetInquiry();
  selectedAgentId.value = null;
  emit('reset');
};

const purposes = [
  { label: t('property.form.schedule_tour') || '物件を見学したい', value: '物件を見学したい' },
  { label: t('property.form.request_more_photos') || 'もっと物件の写真がみたい', value: 'もっと物件の写真がみたい' },
  { label: t('property.form.ask_about_costs') || '初期費用について知りたい', value: '初期費用について知りたい' },
  { label: t('property.form.ask_about_neighborhood') || '設備や周辺環境などについて詳しく聞きたい', value: '設備や周辺環境などについて詳しく聞きたい' },
  { label: t('property.form.search_similar') || '似た条件の物件を探してほしい', value: '似た条件の物件を探してほしい' },
  { label: t('property.form.other') || 'その他問い合わせ', value: 'その他問い合わせ' },
];

function getAvatar(agent: AgentWithProfiles) {
  if (agent.profile?.avatar?.url) return agent.profile.avatar.url;
  if (agent.profile?.avatar?.data) return 'data:image/png;base64,' + agent.profile.avatar.data;
  return '/images/avatar.svg';
}

function getBrokerageLicense(agent: AgentWithProfiles) {
  const g = agent.company?.general;
  if (!g?.brokeragePrefecture || !g?.brokerageLicenceNumber || !g?.brokerageLicenceNumber2) {
    return null;
  }
  return `${g.brokeragePrefecture}免許 (${g.brokerageLicenceNumber}) 第${g.brokerageLicenceNumber2}号`;
}
</script>
